/**
 * BR 驳回理由展示：与 SQL fn_BR_TranslateRejectReason / fn_BR_MapAiRejectCode 对齐。
 * · 优先展示接口已转义文案；若仍是 JSON/短码，前端再兜底转一次。
 * · 人工自由文本原样展示。
 */
const BR_REJECT_REASON_I18N = {
  DISPLAY_NAME: 'lbl_EMPBE_BR_Rejected_Reason_DisplayName',
  OCR_NAME: 'lbl_EMPBE_BR_Rejected_Reason_OcrName',
  REGISTRY_NOT_FOUND: 'lbl_EMPBE_BR_Rejected_Reason_RegistryNotFound',
  REGISTRY_MISMATCH: 'lbl_EMPBE_BR_Rejected_Reason_RegistryMismatch',
  CANCELLED: 'lbl_EMPBE_BR_Rejected_Reason_Cancelled',
  MOVED_OUT: 'lbl_EMPBE_BR_Rejected_Reason_MovedOut',
  REVOKED: 'lbl_EMPBE_BR_Rejected_Reason_Revoked',
  RESCINDED: 'lbl_EMPBE_BR_Rejected_Reason_Rescinded',
  SUSPENDED: 'lbl_EMPBE_BR_Rejected_Reason_Suspended',
  ENT_NAME: 'lbl_EMPBE_BR_Rejected_Reason_EntNameFlag',
  LICENSE_NO: 'lbl_EMPBE_BR_Rejected_Reason_LicenseNoFlag',
  LEGAL_PERSON_NAME: 'lbl_EMPBE_BR_Rejected_Reason_LegalPersonNameFlag',
  LEGAL_PERSON_CERT: 'lbl_EMPBE_BR_Rejected_Reason_LegalPersonCertNoFlag',
  GENERIC: 'lbl_EMPBE_BR_Rejected_Reason_Generic',
};

/** 短码 → i18n（与 SQL CASE 一致；BRCancelled ≈ Status=3 注销，待业务确认） */
const BR_AI_REJECT_CODE_KEYS = {
  BRCANCELLED: BR_REJECT_REASON_I18N.CANCELLED,
  3: BR_REJECT_REASON_I18N.CANCELLED,
  2: BR_REJECT_REASON_I18N.MOVED_OUT,
  4: BR_REJECT_REASON_I18N.REVOKED,
  5: BR_REJECT_REASON_I18N.RESCINDED,
  6: BR_REJECT_REASON_I18N.SUSPENDED,
  ENTNAMEFLAG: BR_REJECT_REASON_I18N.ENT_NAME,
  LICENSENOFLAG: BR_REJECT_REASON_I18N.LICENSE_NO,
  LEGALPERSONNAMEFLAG: BR_REJECT_REASON_I18N.LEGAL_PERSON_NAME,
  LEGALPERSONCERTNOFLAG: BR_REJECT_REASON_I18N.LEGAL_PERSON_CERT,
  'ENTERPRISE NOT FOUND.': BR_REJECT_REASON_I18N.REGISTRY_NOT_FOUND,
  'ENTERPRISE DATA DOES NOT MATCH.': BR_REJECT_REASON_I18N.REGISTRY_MISMATCH,
};

export const BR_REJECT_REASON_MATCHERS = [
  {
    test: (reason) => /enterprise not found in official registry|未在官方企业登记库中查到/i.test(reason),
    key: BR_REJECT_REASON_I18N.REGISTRY_NOT_FOUND,
  },
  {
    test: (reason) => /enterprise data does not match official registry|企业信息与官方登记库记录不一致/i.test(reason),
    key: BR_REJECT_REASON_I18N.REGISTRY_MISMATCH,
  },
  {
    test: (reason) => /registered name does not match ocr/i.test(reason),
    key: BR_REJECT_REASON_I18N.OCR_NAME,
  },
  {
    test: (reason) => /display name does not match registered|company name on your br document does not match/i.test(reason),
    key: BR_REJECT_REASON_I18N.DISPLAY_NAME,
  },
  {
    test: (reason) => /cancelled in the official|已注销状态/i.test(reason),
    key: BR_REJECT_REASON_I18N.CANCELLED,
  },
];

export const BR_REJECT_REASON_GENERIC_KEY = BR_REJECT_REASON_I18N.GENERIC;

/** 管理端 OTHER 选项标签（仅去掉标签本身，绝不能误伤真实驳回正文） */
const ADMIN_OTHER_LABEL_ALT = '選擇手動輸入原因|选择手动输入原因|Select to enter reason manually';
const ADMIN_OTHER_LABEL_ONLY_RE = new RegExp(`^(?:${ADMIN_OTHER_LABEL_ALT})$`, 'i');
/** 「選擇手動輸入原因：23」→「23」；无冒号的孤立标签整段丢弃 */
const ADMIN_OTHER_LABEL_PREFIX_RE = new RegExp(
  `^(?:${ADMIN_OTHER_LABEL_ALT})\\s*[:：]\\s*`,
  'i',
);

/**
 * 供应商展示用：只剥管理端「手动输入」标签前缀，保留 {errorMessage} 正文。
 * 例：選擇手動輸入原因：证件不清晰 → 证件不清晰
 */
export function sanitizeBrRejectReasonForSupplier(rawReason) {
  const text = String(rawReason || '').trim();
  if (!text) {
    return '';
  }
  return text
    .split(/\s*;\s*/)
    .map((part) => {
      const chunk = String(part || '').trim();
      if (!chunk) {
        return '';
      }
      if (ADMIN_OTHER_LABEL_ONLY_RE.test(chunk)) {
        return '';
      }
      return chunk.replace(ADMIN_OTHER_LABEL_PREFIX_RE, '').trim();
    })
    .filter(Boolean)
    .join('; ');
}

export function resolveBrRejectReason(item) {
  return (
    item?.rejectReason
    || item?.RejectReason
    || item?.rejectTitle
    || item?.RejectTitle
    || ''
  ).trim();
}

/**
 * 拆成可映射片段：JSON 数组 / 逗号短码 / 单段正文。
 */
export function extractBrRejectReasonParts(rawReason) {
  const text = String(rawReason || '').trim();
  if (!text) {
    return [];
  }

  if (text.startsWith('[')) {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) {
        return parsed
          .map((row) => String(row?.rejectReason || row?.RejectReason || '').trim())
          .filter(Boolean);
      }
    } catch (_) {
      /* 非 JSON，走下文 */
    }
  }

  if (/^[A-Za-z0-9_.,]+$/.test(text) && text.includes(',')) {
    return text.split(',').map((part) => part.trim()).filter(Boolean);
  }

  return [text];
}

function resolveBrAiRejectCodeKey(code) {
  const normalized = String(code || '').trim();
  if (!normalized) {
    return '';
  }
  const upper = normalized.toUpperCase();
  return BR_AI_REJECT_CODE_KEYS[upper]
    || BR_AI_REJECT_CODE_KEYS[normalized]
    || '';
}

function resolveBrRejectReasonKey(rawReason) {
  const reason = String(rawReason || '').trim();
  if (!reason) {
    return '';
  }
  const fromCode = resolveBrAiRejectCodeKey(reason);
  if (fromCode) {
    return fromCode;
  }
  const matched = BR_REJECT_REASON_MATCHERS.find((entry) => entry.test(reason));
  return matched?.key || '';
}

function isBrMachineRejectToken(part) {
  const chunk = String(part || '').trim();
  if (!chunk) {
    return false;
  }
  // 与 SQL 一致：纯 ASCII 标识视为机器短码；中文/英文自由句保留原文
  return /^[A-Za-z0-9_.,]+$/.test(chunk);
}

function mapBrRejectReasonPart(part, translate) {
  const chunk = String(part || '').trim();
  if (!chunk) {
    return '';
  }
  const key = resolveBrRejectReasonKey(chunk);
  if (key && typeof translate === 'function') {
    return translate(key);
  }
  if (isBrMachineRejectToken(chunk) && typeof translate === 'function') {
    return translate(BR_REJECT_REASON_GENERIC_KEY);
  }
  return chunk;
}

export function resolveBrRejectReasonText(item, translate) {
  const original = resolveBrRejectReason(item);
  const rawReason = sanitizeBrRejectReasonForSupplier(original);
  const parts = extractBrRejectReasonParts(rawReason);
  const mapped = parts
    .map((part) => mapBrRejectReasonPart(part, translate))
    .filter(Boolean);

  if (mapped.length) {
    return mapped.join('; ');
  }

  return typeof translate === 'function'
    ? translate(BR_REJECT_REASON_GENERIC_KEY)
    : '';
}
